// src/commands/createSolidHatchFromBoundary.js
import * as THREE from 'three';
import { generateUniqueId } from '../utils/generateUniqueId';
//import { NURBSCurve } from 'three/examples/jsm/Addons.js';
import { NURBSCurve } from 'three/examples/jsm/curves/NURBSCurve.js';
import {
  generateHatchLines,
  createHatchLineGeometry,
  getStandardPattern,
  extractBoundaryFromShape,
  getBoundingBox,
  findLinePolygonIntersections,
  applyDashPattern
} from '../utils/hatchPatternUtils';

// ✅ Global moveX/moveY kaldırıldı - artık mesh.position olarak uygulanıyor

/**
 * Bir pattern line için grid döngüsü oluşturur ve boundary içinde kalan çizgileri döndürür
 * DXF'den gelen değerler zaten scale uygulanmış halde gelir
 * @param {Object} patternLine - Pattern line tanımı
 * @param {Array} boundary - Boundary noktaları [{x, y}, ...]
 * @returns {Array} Line segment listesi [{start, end}, ...]
 */
function drawLineFamily(patternLine, boundary) {
  const allSegments = [];

  // DXF'den gelen değerleri al
  const baseX = patternLine.LineBasePointX || 0;
  const baseY = patternLine.LineBasePointY || 0;
  const offsetX = patternLine.LineOffsetX || 0;
  const offsetY = patternLine.LineOffsetY || 1;
  const angle = patternLine.lineAngle || 0;
  const dashes = patternLine.dashes || [];
  const scaledDashes = [...dashes];

  // Açıyı radyana çevir
  const angleRad = angle * Math.PI / 180;

  // Çizgi yönü
  const dirX = Math.cos(angleRad);
  const dirY = Math.sin(angleRad);

  // Offset vektörünün uzunluğu
  const offsetLength = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
  if (offsetLength < 0.001) {
    console.warn('⚠️ SKIPPED: offsetLength too small', { angle, offsetX, offsetY, offsetLength });
    return allSegments;
  }

  // Bounding box
  const bounds = getBoundingBox(boundary);

  // Çizgiler arasındaki dik mesafe (cross product)
  // Bu değer offset vektörünün çizgi yönüne dik bileşeni
  const perpSpacing = Math.abs(offsetX * dirY - offsetY * dirX);
  if (perpSpacing < 0.001) {
    console.warn('⚠️ SKIPPED: perpSpacing too small', { angle, offsetX, offsetY, perpSpacing });
    return allSegments;
  }

  // Boundary merkezini referans noktası olarak kullan
  const centerX = bounds.center.x;
  const centerY = bounds.center.y;

  // Merkez noktasının basePoint'e göre konumunu bul
  const toCenterX = centerX - baseX;
  const toCenterY = centerY - baseY;

  // Offset vektörünün çizgi yönüne dik bileşeni (işaretli)
  const offsetPerpComp = offsetX * dirY - offsetY * dirX;

  // Merkez noktasının çizgi yönüne dik mesafesi
  const centerPerpComp = toCenterX * dirY - toCenterY * dirX;

  // Merkeze en yakın çizgi indeksi
  const centerIndex = Math.round(centerPerpComp / offsetPerpComp);

  // Diagonal mesafe ile kaç çizgi gerektiğini hesapla
  const diagonal = Math.sqrt(bounds.width * bounds.width + bounds.height * bounds.height);
  let halfNumLines = Math.ceil(diagonal / perpSpacing) + 2;

  // Güvenlik limiti: devasa hatch alanlarında (örn. vaziyet planı) 40K+ çizgi üretilmesi
  // hem DXF yüklemesini hem de Three.js geometrisini yavaşlatır.
  // 3000 yarı-çizgi = toplam ~6001 çizgi → makul kalite + performans.
  const MAX_HALF_LINES = 3000;
  if (halfNumLines > MAX_HALF_LINES) {
    halfNumLines = MAX_HALF_LINES;
  }

  const minIndex = centerIndex - halfNumLines;
  const maxIndex = centerIndex + halfNumLines;

  // Dash cycle length
  let dashCycleLength = 0;
  scaledDashes.forEach(d => dashCycleLength += Math.abs(d));

  // DEBUG
 
  let intersectionCount = 0;

  // Grid döngüsü - basePoint + i * offset formülü ile çizgi konumları
  for (let i = minIndex; i <= maxIndex; i++) {
    // Çizgi origin: basePoint + i * offset
    // Bu formül grid hizalamasını garanti eder
    const lineOriginX = baseX + offsetX * i;
    const lineOriginY = baseY + offsetY * i;

    const intersections = findLinePolygonIntersections(
      { x: lineOriginX, y: lineOriginY },
      { x: dirX, y: dirY },
      boundary
    );
    intersectionCount += intersections.length;

    for (let j = 0; j < intersections.length - 1; j += 2) {
      const start = intersections[j];
      const end = intersections[j + 1];

      let phaseOffset = 0;
      if (dashCycleLength > 0) {
        // Phase hesaplaması:
        // 1. Start noktasının base point'e göre çizgi yönündeki mesafesi
        // 2. Her paralel çizgi için stagger (kayma) miktarı çıkarılır

        // Start noktasının base point'e göre çizgi yönündeki mesafesi
        const toStartX = start.x - baseX;
        const toStartY = start.y - baseY;
        const distFromBase = toStartX * dirX + toStartY * dirY;

        // Stagger: offset vektörünün çizgi yönündeki bileşeni
        // Bu değer her paralel çizgide desenin ne kadar kaydığını belirler (tuğla deseni için)
        const stagger = offsetX * dirX + offsetY * dirY;

        // Phase = base noktasından mesafe - (çizgi indeksi × stagger)
        // Stagger çıkarılır çünkü her sonraki çizgide desen ileri kayar
        phaseOffset = distFromBase - stagger * i;
      }

      const segments = applyDashPattern(start, end, scaledDashes, 1, phaseOffset);

      // DEBUG: İlk birkaç segment'i logla
      if (allSegments.length < 3 && segments.length > 0) {
 
      }

      for (let s = 0; s < segments.length; s++) allSegments.push(segments[s]);
    }
  }

   return allSegments;
}

function colorToHexString(color) {
  if (typeof color === 'number') {
    // 24-bit RGB'den fazlasını kes
    const rgb = color & 0xffffff;
    return '#' + rgb.toString(16).padStart(6, '0');
  }
  return color;
}

export function createSolidHatchFromBoundary(element, {
  boundaryPaths = [],
  color = 0xff0000,
  pattern,
  scene,
  group,
  moveX = 0,
  moveY = 0,
  layerName = '0',
}) {
  if (!boundaryPaths || boundaryPaths.length === 0) {
    console.error("createSolidHatchFromBoundary: Invalid boundaryPaths", boundaryPaths);
    return null;
  }
  
   const shapes = [];

  // ✅ Geometriyi origin'de oluştur (moveX=0, moveY=0)
  // Pozisyon mesh.position olarak uygulanacak (normalizasyon için)
  boundaryPaths.forEach((outerBoundary) => {
    if (outerBoundary.type !== "outer") return;

    const shape = new THREE.Shape();

    try {
      addPathToShape(outerBoundary.groups, shape, 0, 0); // ✅ Tüm edge'leri tek seferde işle
    } catch (err) {
      console.error("addPathToShape error:", err);
    }

    // innerGroups varsa, onları hole olarak ekle
    outerBoundary.innerGroups?.forEach((innerBoundary) => {
      const holeShape = new THREE.Shape();
      try {
        addPathToShape(innerBoundary.groups, holeShape, 0, 0); // ✅ Tüm edge'leri tek seferde işle
      } catch (err) {
        console.error("inner path error:", err);
      }
      shape.holes.push(holeShape);
    });

    shapes.push(shape);
  });

  if (shapes.length === 0) return null;

  // DXF standardına göre solid/pattern kontrolü:
  // - SolidFillFlag = 1 VE HatchPatternName = "SOLID" → Solid fill
  // - Diğer durumlar → Pattern fill
  const isSolid = element.SolidFillFlag === 1 && element.HatchPatternName === "SOLID";

 

  if (isSolid) {
    // SOLID FILL - Mesh kullan
    const geometry = new THREE.ShapeGeometry(shapes);
    const material = new THREE.MeshBasicMaterial({
      color: typeof color === 'string' ? new THREE.Color(color.trim()) : new THREE.Color(color),
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);

    // ✅ Hatch base point'i mesh pozisyonu olarak uygula (normalizasyon)
    mesh.position.set(moveX, moveY, 0);

    mesh.userData = {
      id: generateUniqueId('hatch'),  // ✅ Seçim sistemi için id ekle
      type: "hatch",
      boundaryPaths: boundaryPaths,
      dxfId: element.handle,
      lineType: element.LineType || "ByLayer",
      isSelectable: true,
      layerName,
      // DXF export için hatch tipi bilgileri
      SolidFillFlag: 1,
      HatchPatternName: "SOLID",
      // ✅ Base point bilgisini sakla (DXF export için)
      basePoint: { x: moveX, y: moveY },
    };

    if (group) group.add(mesh);
    else if (scene) scene.add(mesh);

    return mesh;
  } else {
    // PATTERN FILL - Geometry-based çizgiler kullan
    const patternScale = element.HatchPatternScale || 1;
    const patternAngle = element.HatchPatternAngle || 0;
    const patternName = element.HatchPatternName || '';

    // element.pattern içindeki patternLine dizisini al
    const patterns = element.pattern?.patternLine || pattern?.patternLine || [];

 

    // Her shape için hatch çizgileri oluştur
    const hatchGroup = new THREE.Group();

    // ✅ Hatch base point'i group pozisyonu olarak uygula (normalizasyon)
    hatchGroup.position.set(moveX, moveY, 0);

    hatchGroup.userData = {
      id: generateUniqueId('hatch'),  // ✅ Seçim sistemi için id ekle
      type: "hatch",
      boundaryPaths: boundaryPaths,
      dxfId: element.handle,
      lineType: element.LineType || "ByLayer",
      isSelectable: true,
      layerName,
      patternName: patternName,
      // DXF export için hatch tipi bilgileri
      SolidFillFlag: element.SolidFillFlag ?? 0,
      HatchPatternName: element.HatchPatternName || patternName,
      HatchPatternScale: patternScale,
      HatchPatternAngle: patternAngle,
      // Pattern tanımları (DXF export için)
      pattern: element.pattern || (patterns.length > 0 ? { patternLine: patterns } : undefined),
      // Pattern hatch'in çocuklarının ayrı export edilmesini engelle
      isPatternHatch: true,
      // ✅ Base point bilgisini sakla (DXF export için)
      basePoint: { x: moveX, y: moveY },
    };

    shapes.forEach((shape, index) => {
      // Shape'den boundary noktalarını çıkar
      const boundary = extractBoundaryFromShape(shape, 64);

      if (boundary.length < 3) return;

      let allSegments = [];

      // element.pattern varsa yeni drawLineFamily mekanizmasını kullan
      if (patterns.length > 0) {
        // Her patternLine için drawLineFamily çağır
        patterns.forEach((patternLine, idx) => {
          const segments = drawLineFamily(patternLine, boundary);
          for (let s = 0; s < segments.length; s++) allSegments.push(segments[s]);
        });
      } else {
        // Standart pattern veya fallback kullan
        let patternDef = getStandardPattern(patternName);

        if (!patternDef) {
          // Fallback: Basit 45 derece çizgiler
          patternDef = {
            lines: [{ angle: 45, basePoint: [0, 0], offset: [0, 3], dashes: [] }]
          };
        }

        // Eski generateHatchLines fonksiyonunu kullan
        allSegments = generateHatchLines(boundary, patternDef, patternScale, patternAngle);
      }

      if (allSegments.length > 0) {
        // Line geometry oluştur
        const lineColor = typeof color === 'string'
          ? new THREE.Color(color.trim()).getHex()
          : (typeof color === 'number' ? color : 0x000000);

        const lineGeometry = createHatchLineGeometry(allSegments, lineColor, 0.05);

        if (lineGeometry) {
          hatchGroup.add(lineGeometry);
        }
      }
    });

    if (hatchGroup.children.length === 0) {
      // Hiç çizgi oluşturulamadıysa uyarı ver ama solid mesh EKLEME
/*       console.warn('⚠️ Pattern hatch için çizgi oluşturulamadı:', {
        patternName: element.HatchPatternName,
        patternsCount: patterns.length
      }); */
    }

    if (group) group.add(hatchGroup);
    else if (scene) scene.add(hatchGroup);

    return hatchGroup;
  }
}


function addPathToShape(path, shape, moveX = 0, moveY = 0) {
  if (Array.isArray(path)) {
    path.forEach((segment, i) => {
      processPathSegment(segment, shape, i, moveX, moveY, path);
    });
  } else {
    processPathSegment(path, shape, 0, moveX, moveY, null);
  }
}


function createHatchPatternMaterial81(pattern, patternScale = 1, patternSpacing = 1) {
  const MAX_LINES = 20;
  const MAX_DASHES = 10;
  const lineCount = pattern?.patternLine ? Math.min(pattern.patternLine.length, MAX_LINES) : 1;

  const lineAngles = new Float32Array(MAX_LINES);
  const lineOffsetsX = new Float32Array(MAX_LINES);
  const lineOffsetsY = new Float32Array(MAX_LINES);
  const dashCounts = new Float32Array(MAX_LINES);
  const dashes = new Float32Array(MAX_LINES * MAX_DASHES);
  const lineSpacings = new Float32Array(MAX_LINES);

  for (let i = 0; i < lineCount; i++) {
    const line = pattern.patternLine[i];
    const angleRad = THREE.MathUtils.degToRad(line.lineAngle || 0.0);
    lineAngles[i] = angleRad;

    const lineOffsetX = (2 * line.LineOffsetX || 0.0) * patternScale;
    const lineOffsetY = (2 * line.LineOffsetY || 0.0) * patternScale;
    lineOffsetsX[i] = lineOffsetX;
    lineOffsetsY[i] = lineOffsetY;

    dashCounts[i] = Math.min(line.dashCount || 0, MAX_DASHES);
    for (let j = 0; j < dashCounts[i]; j++) {
      dashes[i * MAX_DASHES + j] = line.dashes[j] * patternScale;
    }

    const spacing = Math.abs(lineOffsetX * (-Math.sin(angleRad)) + lineOffsetY * Math.cos(angleRad));
    lineSpacings[i] = spacing * 0.05 || 0.0;
  }

  const defaultColor = 0x000000; // sabit çizgi rengi (siyah)
  const defaultZoom = 1.0; // sabit çizgi kalınlığı

  const hatchMaterial = new THREE.ShaderMaterial({
    uniforms: {
      color1: { value: new THREE.Color(defaultColor) },
      lineCount: { value: lineCount },
      lineAngles: { value: lineAngles },
      dashCounts: { value: dashCounts },
      dashes: { value: dashes },
      lineSpacings: { value: lineSpacings },
      lineWidth: { value: defaultZoom },
      lineOffsetsX: { value: lineOffsetsX },
      lineOffsetsY: { value: lineOffsetsY },
    },
    transparent: true,
    side: THREE.DoubleSide,
    vertexShader: `
        varying vec2 vUV;
        void main() {
            vUV = uv;
            gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        varying vec2 vUV;
        uniform vec3 color1;
        uniform float lineCount;
        uniform float lineAngles[20];
        uniform float dashCounts[20];
        uniform float dashes[200];
        uniform float lineSpacings[20];
        uniform float lineWidth;
        uniform float lineOffsetsX[20];
        uniform float lineOffsetsY[20];

        void main() {
            bool found = false;
            for (int i = 0; i < int(lineCount); i++) {
                vec2 shiftedUV = vUV;
                shiftedUV.x += lineOffsetsX[i];
                shiftedUV.y += lineOffsetsY[i];

                float cosAngle = cos(lineAngles[i]);
                float sinAngle = sin(lineAngles[i]);

                float rotatedX = shiftedUV.x * cosAngle + shiftedUV.y * sinAngle;
                float rotatedY = shiftedUV.x * sinAngle - shiftedUV.y * cosAngle;

                float spacing = lineSpacings[i];
                if (spacing > 0.0) {
                    rotatedY = mod(rotatedY, spacing);
                }

                if (abs(rotatedY) > lineWidth) continue;

                if (dashCounts[i] == 0.0) {
                    found = true;
                    break;
                }

                float totalCycle = 0.0;
                for (int j = 0; j < 10; j++) {
                    if (float(j) >= dashCounts[i]) break;
                    float segmentLength = abs(dashes[i * 10 + j]);
                    if (segmentLength <= 0.0) continue;
                    totalCycle += segmentLength;
                }

                if (totalCycle <= 0.0) continue;

                float cyclePos = mod(rotatedX, totalCycle);
                float accumulated = 0.0;
                bool isDash = false;

                for (int j = 0; j < 10; j++) {
                    if (float(j) >= dashCounts[i]) break;
                    float segmentLength = abs(dashes[i * 10 + j]);
                    if (segmentLength <= 0.0) continue;
                    accumulated += segmentLength;
                    if (cyclePos < accumulated) {
                        isDash = dashes[i * 10 + j] > 0.0;
                        break;
                    }
                }

                if (isDash) {
                    found = true;
                    break;
                }
            }

            if (found) {
                gl_FragColor = vec4(color1, 1.0);
            } else {
                discard;
            }
        }
    `
  });

  hatchMaterial.color = hatchMaterial.uniforms.color1.value;
  return hatchMaterial;
}
/**
 * Bir edge segmentinin başlangıç noktasını döndürür (CircularArc/EllipticArc hesaplamaları için)
 */
function getEdgeStartPoint(segment, moveX, moveY) {
  if (!segment) return null;
  switch (segment.edgeType) {
    case 'Line':
      if (segment.bulge === true) {
        if (segment.points && segment.points.length >= 2) {
          return { x: segment.points[0] + moveX, y: segment.points[1] + moveY };
        }
      }
      if (segment.x1 !== undefined && segment.y1 !== undefined) {
        return { x: segment.x1 + moveX, y: segment.y1 + moveY };
      }
      return null;
    case 'Polyline':
      if (segment.points && segment.points.length >= 2) {
        return { x: segment.points[0] + moveX, y: segment.points[1] + moveY };
      }
      return null;
    default:
      return null;
  }
}

/**
 * Bir noktanın döndürülmüş elips üzerindeki parametrik açısını hesaplar
 */
function pointToEllipseParam(px, py, cx, cy, a, b, rotation) {
  const dx = px - cx;
  const dy = py - cy;
  const cosR = Math.cos(rotation);
  const sinR = Math.sin(rotation);
  // Elipsin lokal koordinat sistemine geri döndür
  const rx = dx * cosR + dy * sinR;
  const ry = -dx * sinR + dy * cosR;
  return Math.atan2(ry / b, rx / a);
}

function processPathSegment(pathSegment, shape, index, moveX = 0, moveY = 0, allSegments = null) {

  switch (pathSegment.edgeType) {
    case 'Line':
      if (index === 0 && !pathSegment.bulge) {
        // İlk noktaya moveTo ile gidiyoruz (sadece non-bulge line'lar için)
        // Bulge durumunda moveTo aşağıda a===0 bloğunda yapılır
        let startX = pathSegment.x1;
        let startY = pathSegment.y1;
        // ✅ FIX: Bazı DXF edge boundary'lerinde ilk edge'in startY'si (group 20) eksik olabiliyor
        // Bu durumda kapalı sınırın son edge'inin bitiş noktasını kullan
        if ((startY === undefined || startY === null) && allSegments && allSegments.length > 0) {
          const lastSeg = allSegments[allSegments.length - 1];
          if (lastSeg.x2 !== undefined) startX = lastSeg.x2;
          if (lastSeg.y2 !== undefined) startY = lastSeg.y2;
        }
        if (startX !== undefined && startY !== undefined) {
          shape.moveTo(startX + moveX, startY + moveY);
        }
      }
      if (pathSegment.bulge === true) {
        let startpoint;
        for (var a = 0; a <= pathSegment.NumberOfPolylineVertices - 1; a++) {
          let p1, p2, bulge;
          let lp1, lp2;
          p1 = new THREE.Vector2(pathSegment.points[a * 3] + moveX, pathSegment.points[a * 3 + 1] + moveY);
          p2 = new THREE.Vector2(pathSegment.points[3 + a * 3] + moveX, pathSegment.points[4 + a * 3] + moveY);
          bulge = pathSegment.points[a * 3 + 2];
          if (a === 0) {
            startpoint = p1;
            shape.moveTo(p1.x, p1.y); // İlk noktayı doğru şekilde işaretliyoruz
          }
          if (a === pathSegment.NumberOfPolylineVertices - 1 && pathSegment.IsClosedFlag == 1) {
            lp2 = new THREE.Vector2(pathSegment.points[0] + moveX, pathSegment.points[1] + moveY); // Döngü sonundaki nokta
            lp1 = p1;
          } else {
            lp1 = p1;
            lp2 = p2;
          }
          // Bulge değerine göre yay veya çizgi çizeriz
          if (bulge === 0) {
            shape.lineTo(lp2.x, lp2.y); // Düz çizgi
          } else {
            const distance = lp1.distanceTo(lp2);
            // Noktalar çakışıyorsa veya mesafe çok küçükse yay çizilemez
            if (distance < 1e-6) {
              continue;
            }
            const sagitta = (bulge * distance) / 2;
            // Sagitta sıfır veya çok küçükse düz çizgi çiz
            if (Math.abs(sagitta) < 1e-6) {
              shape.lineTo(lp2.x, lp2.y);
              continue;
            }
            const radius = (distance ** 2 / (8 * sagitta)) + (sagitta / 2);
            // Geçersiz veya aşırı büyük yarıçap kontrolü
            if (!isFinite(radius) || Math.abs(radius) > distance * 1000) {
              shape.lineTo(lp2.x, lp2.y);
              continue;
            }
            const midPoint = new THREE.Vector2(
              (lp1.x + lp2.x) / 2,
              (lp1.y + lp2.y) / 2
            );
            const normalVec = new THREE.Vector2(
              -(lp2.y - lp1.y),
              lp2.x - lp1.x
            );
            const normalLen = normalVec.length();
            if (normalLen < 1e-6) {
              shape.lineTo(lp2.x, lp2.y);
              continue;
            }
            normalVec.divideScalar(normalLen);
            const center = midPoint.clone().add(normalVec.multiplyScalar(radius - sagitta));
            const startAngle = Math.atan2(lp1.y - center.y, lp1.x - center.x);
            const endAngle = Math.atan2(lp2.y - center.y, lp2.x - center.x);
            const clockwise = bulge > 0;
            shape.absarc(center.x, center.y, Math.abs(radius), startAngle, endAngle, !clockwise);
          }
        }
      } else {
        shape.lineTo(pathSegment.x2 + moveX, pathSegment.y2 + moveY);
      }
      break;
    case 'Polyline':
      let startPoint;
      const numVertices = pathSegment.NumberOfPolylineVertices;
      for (let a = 0; a < numVertices; a++) {
        let p1 = new THREE.Vector2(pathSegment.points[a * 2] + moveX, pathSegment.points[a * 2 + 1] + moveY);
        let p2 = new THREE.Vector2(pathSegment.points[((a + 1) % numVertices) * 2] + moveX, pathSegment.points[((a + 1) % numVertices) * 2 + 1] + moveY);
        if (a === 0) {
          startPoint = p1;
          shape.moveTo(p1.x, p1.y);
        }
        shape.lineTo(p2.x, p2.y);
      }
      if (pathSegment.IsClosedFlag == 1) {
        shape.lineTo(startPoint.x, startPoint.y);
      }
      break;

    case 'CircularArc': {
      const arcCx = pathSegment.x + moveX;
      const arcCy = pathSegment.y + moveY;
      const arcR = pathSegment.radius;

      let arcStartAngle, arcEndAngle, arcClockwise;
      let arcAnglesComputed = false;

      // Komşu edge noktalarından açıları hesapla (daha güvenilir)
      const arcCp = shape.currentPoint;
      if (index > 0 && arcCp && allSegments) {
        const computedStart = Math.atan2(arcCp.y - arcCy, arcCp.x - arcCx);

        let computedEnd;
        if (index < allSegments.length - 1) {
          const nextStart = getEdgeStartPoint(allSegments[index + 1], moveX, moveY);
          if (nextStart) computedEnd = Math.atan2(nextStart.y - arcCy, nextStart.x - arcCx);
        } else if (allSegments.length > 1) {
          const firstStart = getEdgeStartPoint(allSegments[0], moveX, moveY);
          if (firstStart) computedEnd = Math.atan2(firstStart.y - arcCy, firstStart.x - arcCx);
        }

        if (computedEnd !== undefined) {
          arcStartAngle = computedStart;
          arcEndAngle = computedEnd;
          // DXF CCW=0 → clockwise, CCW=1 → counterclockwise
          // Three.js aClockwise: true=CW, false=CCW
          arcClockwise = !pathSegment.IsCounterclockwiseFlag;
          arcAnglesComputed = true;
        }
      }

      if (!arcAnglesComputed) {
        // Fallback: DXF açılarını kullan (mevcut davranış)
        arcStartAngle = pathSegment.StartAngle / 180 * Math.PI;
        arcEndAngle = pathSegment.EndAngle / 180 * Math.PI;
        arcClockwise = pathSegment.IsCounterclockwiseFlag;
      }

      if (index === 0) {
        shape.moveTo(
          arcCx + arcR * Math.cos(arcStartAngle),
          arcCy + arcR * Math.sin(arcStartAngle)
        );
      }
      shape.absarc(arcCx, arcCy, arcR, arcStartAngle, arcEndAngle, arcClockwise);
      break;
    }

    case 'EllipticArc': {
      const ellCx = parseFloat(pathSegment.x) + moveX;
      const ellCy = parseFloat(pathSegment.y) + moveY;

      // Major axis endpoint DXF'te merkeze göre RELATIF
      const majX = parseFloat(pathSegment.EndPointX);
      const majY = parseFloat(pathSegment.EndPointY);
      const ellRotation = Math.atan2(majY, majX);
      const majorLen = Math.sqrt(majX * majX + majY * majY);
      const ellRatio = pathSegment.ratioOfMinor;
      const minorLen = majorLen * ellRatio;

      let ellStartParam, ellEndParam;
      let ellAnglesComputed = false;

      // Komşu edge noktalarından parametrik açıları hesapla
      const ellCp = shape.currentPoint;
      if (index > 0 && ellCp && allSegments && majorLen > 1e-10 && minorLen > 1e-10) {
        const computedEllStart = pointToEllipseParam(ellCp.x, ellCp.y, ellCx, ellCy, majorLen, minorLen, ellRotation);

        let computedEllEnd;
        if (index < allSegments.length - 1) {
          const nextStart = getEdgeStartPoint(allSegments[index + 1], moveX, moveY);
          if (nextStart) {
            computedEllEnd = pointToEllipseParam(nextStart.x, nextStart.y, ellCx, ellCy, majorLen, minorLen, ellRotation);
          }
        } else if (allSegments.length > 1) {
          const firstStart = getEdgeStartPoint(allSegments[0], moveX, moveY);
          if (firstStart) {
            computedEllEnd = pointToEllipseParam(firstStart.x, firstStart.y, ellCx, ellCy, majorLen, minorLen, ellRotation);
          }
        }

        if (computedEllEnd !== undefined) {
          ellStartParam = computedEllStart;
          ellEndParam = computedEllEnd;
          ellAnglesComputed = true;
        }
      }

      if (!ellAnglesComputed) {
        // Fallback: mevcut getEllipseRadyan dönüşümü
        let direc = 1;
        let angleA = pathSegment.EndAngle;
        let angleB = pathSegment.StartAngle;
        if (pathSegment.EndPointX < 0) {
          direc = -1;
        }
        ellEndParam = getEllipseRadyan(majorLen, minorLen, angleA, direc);
        ellStartParam = getEllipseRadyan(majorLen, minorLen, angleB, direc);
      }

      if (index === 0) {
        // İlk segment: moveTo ile arc başlangıcına git
        const sx = ellCx + majorLen * Math.cos(ellStartParam) * Math.cos(ellRotation) - minorLen * Math.sin(ellStartParam) * Math.sin(ellRotation);
        const sy = ellCy + majorLen * Math.cos(ellStartParam) * Math.sin(ellRotation) + minorLen * Math.sin(ellStartParam) * Math.cos(ellRotation);
        shape.moveTo(sx, sy);
      }

      shape.absellipse(
        ellCx, ellCy,
        majorLen, minorLen,
        ellStartParam, ellEndParam,
        !pathSegment.IsCounterclockwiseFlag,
        ellRotation
      );
      break;
    }

    case 'Spline':
      // Spline parametrelerini al
      const SPdegree = pathSegment.Degree || 3; // Varsayılan derece 3
      let SPknots = pathSegment.knots ? pathSegment.knots.map(knot => parseFloat(knot)) : [];

      // Kontrol noktalarını al
      const SPcontrolPoints = [];
      for (let i = 0; i < pathSegment.points.length; i += 2) {
        const x = parseFloat(pathSegment.points[i]) + moveX;
        const y = parseFloat(pathSegment.points[i + 1]) + moveY;
        SPcontrolPoints.push(new THREE.Vector2(x, y));
      }
      // Knot vektörünün uzunluğunu hesapla
      const SPh = SPcontrolPoints.length - SPdegree - 1;
      // Knot vektörünün uzunluğunu kontrol et ve gerektiğinde düzelt
      if (SPknots.length !== SPcontrolPoints.length + SPdegree + 1) {
        console.warn("Knot vektörünün uzunluğu kontrol noktalarına ve dereceye uygun değil. Düzeltme yapılıyor.");
        // Knot vektörünü uygun uzunlukta oluştur
        SPknots = [];
        const n = SPcontrolPoints.length;
        const k = SPdegree;
        const m = n + k + 1;
        for (let i = 0; i < m; i++) {
          if (i < k) {
            SPknots.push(0);
          } else if (i > m - k - 1) {
            SPknots.push(1);
          } else {
            SPknots.push((i - k) / (m - 2 * k - 1));
          }
        }
      }

      // Knot vektörünü normalize et (son knot değeri 1 olacak şekilde)
      const lastKnot = SPknots[SPknots.length - 1];
      SPknots = SPknots.map(knot => knot / lastKnot);

      // NURBSCurve oluştur
      let SPnurbsCurve;
      try {
        SPnurbsCurve = new NURBSCurve(SPdegree, SPknots, SPcontrolPoints, SPh);
      } catch (error) {
        console.error("NURBSCurve oluşturulurken hata oluştu:", error);
        break;
      }

      // Başlangıç ve bitiş knotlarını ayarla
      SPnurbsCurve.startKnot = 0;
      SPnurbsCurve.endKnot = SPknots.length - 1;

      // Eğri noktalarını al
      const SPlinePoints = SPnurbsCurve.getPoints(100); // Nokta sayısını arttırabilirsiniz

      if (index === 0 && SPlinePoints.length > 0) {
        shape.moveTo(SPlinePoints[0].x, SPlinePoints[0].y);
      }

      SPlinePoints.forEach(p => {
        shape.lineTo(p.x, p.y);
      });
      break;
    default:
      console.warn('Unknown edge type:', pathSegment.edgeType);
      break;
  }
}
function getEllipseRadyan(majorAxisLength, minorAxisLength, angle0, direc) {
  const totalLenght = ellipseCevre(majorAxisLength, minorAxisLength)
  const X1 = getElipsX1(majorAxisLength, minorAxisLength, angle0)
  let bolge
  let radyan = null
  let angle = angle0
  if (direc === -1) {
    angle = 180 - angle
  }
  if (angle >= 0 && angle < 90) {
    bolge = 1;
    const length = ellipseArcLength(majorAxisLength, minorAxisLength, X1)
    radyan = 2 * Math.PI - direc * (2 * Math.PI * length) / (totalLenght)

  } else if (angle >= 90 && angle < 180) {
    bolge = 2;
    const length = ellipseArcLength(majorAxisLength, minorAxisLength, -X1)
    radyan = (2 * Math.PI * (length + totalLenght / 2)) / (totalLenght)

  } else if (angle <= -90 && angle > -180) {
    bolge = 3;
    const length = ellipseArcLength(majorAxisLength, minorAxisLength, X1)
    radyan = direc * (2 * Math.PI * length) / (totalLenght)
  } else {
    bolge = 4;
    const length = ellipseArcLength(majorAxisLength, minorAxisLength, X1)
    radyan = (2 * Math.PI * length) / (totalLenght)

  }

  return radyan
}
function ellipseArcLength(majorAxisLength, minorAxisLength, X, steps = 50) {
  const f2 = Math.acos(X / majorAxisLength)
  const Df = f2 / steps
  const Df_3 = Df / 3
  let totalLenght = 0;
  for (var a = 0; a <= steps; a++) {
    let k = 1
    if (a > 0 && a < steps) {
      k = a % 2 + 2
      if (k == 3) { k++ }
    } else {
      k = 1
    }
    const Df1 = a * Df
    const sinDF1 = majorAxisLength * Math.sin(Df1)
    const cosDF1 = minorAxisLength * Math.cos(Df1)
    const Yn = k * Math.sqrt((sinDF1 ** 2 + cosDF1 ** 2))
    totalLenght += Yn
  }

  const lenght = totalLenght * Df_3

  return lenght;
}
function getElipsX1(major, minor, angle) {
  const valSin = Math.sin(angle * Math.PI / 180)
  const valCos = Math.cos(angle * Math.PI / 180)
  const valMin = (valSin ** 2) / (minor ** 2)
  const valMaj = (valCos ** 2) / (major ** 2)
  const subVal = 1 / (valMin + valMaj)
  const X1 = Math.sqrt(subVal) * valCos
  return X1
}
function ellipseCevre(majorAxisLength, minorAxisLength) {
  const h = (majorAxisLength * majorAxisLength + minorAxisLength * minorAxisLength);
  const h1 = Math.sqrt(h / 2);
  const h2 = 2 * Math.PI * h1;
  return h2;
}